import React, { useCallback, useEffect, useRef, useState } from 'react';
import { APP_STYLE_CONFIG } from '@/constants/config';

/**
 * Configuration for useAnimatedHeightTransition.
 */
interface UseAnimatedHeightTransitionProps {
  /**
   * Dependency values that represent content-shape changes.
   *
   * After prepareForTransition() is called, any dependency change will trigger
   * a next-frame re-measure so the wrapper can animate from the previous
   * measured height to the new measured height.
   */
  dependencies: React.DependencyList;

  /**
   * Optional custom strategy for capturing the current height before content
   * changes. This is useful for special states such as collapsed sections that
   * should animate from 0 rather than from the current DOM height.
   */
  getCurrentHeight?: (element: HTMLDivElement | null) => number | undefined;

  /**
   * Optional custom strategy for measuring the destination height after content
   * changes. This allows callers to animate to controlled values (for example,
   * collapsing to exactly 0) while still reusing the same transition lifecycle.
   */
  getNextHeight?: (element: HTMLDivElement | null) => number | undefined;
}

/**
 * Public API returned by useAnimatedHeightTransition.
 */
interface UseAnimatedHeightTransitionReturn {
  /** Ref to the inner content node whose natural height is measured. */
  contentRef: React.RefObject<HTMLDivElement>;

  /**
   * Pixel height to apply to the wrapper during transitions.
   *
   * When undefined, the wrapper should render with natural auto height.
   */
  height: number | undefined;

  /** True while the hook is actively coordinating a transition. */
  isAnimating: boolean;

  /**
   * Transition-end handler for the wrapper. Clears temporary height state once
   * the wrapper finishes animating its height.
   */
  handleTransitionEnd: (event: React.TransitionEvent<HTMLDivElement>) => void;

  /**
   * Captures the current wrapper height and starts the transition lifecycle.
   * Call this immediately before toggling state that changes content height.
   */
  prepareForTransition: () => void;
}

/**
 * Coordinates smooth height transitions for content that normally renders at
 * auto height.
 *
 * Why this hook exists:
 * CSS cannot interpolate from `height: auto` to another `height: auto` value.
 * This hook provides a measured-height bridge:
 *  1) capture current height
 *  2) apply next measured height
 *  3) release back to auto after transition end.
 */
export const useAnimatedHeightTransition = ({
  dependencies,
  getCurrentHeight,
  getNextHeight,
}: UseAnimatedHeightTransitionProps): UseAnimatedHeightTransitionReturn => {
  const shouldAnimateHeight = APP_STYLE_CONFIG.useAnimatedHeightTransition;

  // Inner node whose natural height is used as the source of truth.
  const contentRef = useRef<HTMLDivElement>(null);

  // Tracks the pending requestAnimationFrame callback for cleanup.
  const animationFrameRef = useRef<number | null>(null);

  // Temporary fixed height applied to the outer wrapper during transition.
  const [height, setHeight] = useState<number>();

  // Guards re-measure logic so we only measure when transitions are requested.
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!shouldAnimateHeight) return;

    // Do nothing until a transition has been explicitly started.
    if (!isAnimating) return;

    // Wait one frame for React to commit the new content before measuring.
    animationFrameRef.current = window.requestAnimationFrame(() => {
      const nextHeight = getNextHeight
        ? getNextHeight(contentRef.current)
        : contentRef.current?.getBoundingClientRect().height;

      // Missing measurement means we should fail safe and stop animating.
      if (typeof nextHeight !== 'number') {
        setHeight(undefined);
        setIsAnimating(false);
        return;
      }

      // This assignment drives the wrapper's CSS height transition.
      setHeight(nextHeight);
    });

    return () => {
      // Prevent stale callbacks from updating state after dependency changes.
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [dependencies, getNextHeight, isAnimating, shouldAnimateHeight]);

  /**
   * Starts an animation cycle by locking the wrapper to its current height.
   */
  const prepareForTransition = useCallback(() => {
    if (!shouldAnimateHeight) {
      setHeight(undefined);
      setIsAnimating(false);
      return;
    }

    const currentHeight = getCurrentHeight
      ? getCurrentHeight(contentRef.current)
      : contentRef.current?.getBoundingClientRect().height;

    // If we cannot measure, skip animation and leave layout unmanaged.
    if (typeof currentHeight !== 'number') return;

    setHeight(currentHeight);
    setIsAnimating(true);
  }, [getCurrentHeight, shouldAnimateHeight]);

  /**
   * Ends the animation cycle after the wrapper's height transition completes.
   */
  const handleTransitionEnd = useCallback((event: React.TransitionEvent<HTMLDivElement>) => {
    // Ignore transition events for unrelated CSS properties.
    if (event.propertyName !== 'height') return;

    // Release fixed-height mode so layout returns to natural sizing.
    setHeight(undefined);
    setIsAnimating(false);
  }, []);

  // Return stable API refs for use in the consuming component
  return {
    contentRef,
    height,
    handleTransitionEnd,
    isAnimating,
    prepareForTransition,
  };
};

export default useAnimatedHeightTransition;
